/**
 * Maps over the entries of an object and applies a callback function to each entry.
 *
 * @template Key - The type of the keys in the record. Can be string, number, or symbol.
 * @template Value - The type of the values in the record.
 * @template Return - The type of the return value from the callback function.
 *
 * @param {Record<Key, Value>} record - The object whose entries will be mapped over.
 * @param {(entry: [Key, Value]) => Return} callback - The function to apply to each entry.
 * It receives an entry as a tuple [key, value] and returns a value of type Return.
 *
 * @returns {Return[]} An array of the results of applying the callback function to each entry.
 */
export function mapObject<Key extends string | number | symbol, Value, Return>(
  record: Record<Key, Value>,
  callback: (entry: [Key, Value]) => Return,
): Return[] {
  return Object.entries(record).map(([key, value]) =>
    callback([key as Key, value as Value]),
  );
}
